import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
} from "recharts";
import { Target, CheckCircle2, XCircle, ListChecks, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { SUBJECT_LABELS, DIFFICULTY_LABELS, fmtTime, EXAM_LABELS } from "@/lib/pyqMeta";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const KpiCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="rounded-2xl border border-border bg-card p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icon className={`h-4 w-4 ${accent || ""}`} />
      <span className="text-xs font-medium uppercase tracking-[0.15em]">{label}</span>
    </div>
    <div className="mt-3 font-heading text-3xl font-bold">{value}</div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-xl border border-border bg-popover px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold">{label}</div>
      <div className="text-muted-foreground">
        {d.correct}/{d.attempted} correct · {d.accuracy}%
      </div>
    </div>
  );
};

const BreakdownChart = ({ title, data, labels, testid }) => {
  const rows = data.map((d) => ({ ...d, label: labels[d.name] || d.name }));
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid={testid}>
      <h3 className="font-heading text-lg font-semibold">{title}</h3>
      {rows.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">No data yet.</p>
      ) : (
        <div className="mt-4 h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 16 }}>
              <XAxis type="number" domain={[0, 100]} hide />
              <YAxis
                type="category"
                dataKey="label"
                width={96}
                tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip cursor={{ fill: "hsl(var(--secondary))" }} content={<ChartTooltip />} />
              <Bar dataKey="accuracy" radius={[0, 6, 6, 0]} barSize={22}>
                {rows.map((_, i) => (
                  <Cell key={i} fill="hsl(var(--primary))" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default function Stats() {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/stats"),
      api.get("/sessions"),
    ])
      .then(([s, ses]) => {
        setStats(s.data);
        setSessions(ses.data);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <Skeleton className="mb-8 h-10 w-56" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  const empty = !stats || stats.totalAttempted === 0;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">My Progress</h1>
          <p className="mt-2 text-muted-foreground">Your PYQ practice performance so far.</p>
        </div>
        <Button asChild className="rounded-full px-6" data-testid="stats-practice-btn">
          <Link to="/student/practice/pyq">Practise now</Link>
        </Button>
      </div>

      {empty ? (
        <div className="rounded-3xl border border-dashed border-border bg-card p-16 text-center" data-testid="stats-empty">
          <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary text-primary">
            <TrendingUp className="h-7 w-7" />
          </span>
          <p className="font-heading text-xl font-semibold">No attempts yet</p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
            Start a practice session and your accuracy, subject breakdown and session history will
            appear here.
          </p>
          <Button asChild className="mt-6 rounded-full px-6">
            <Link to="/student/practice/pyq">Start practising</Link>
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiCard icon={Target} label="Accuracy" value={`${stats.accuracy}%`} sub="Overall" accent="text-primary" />
            <KpiCard icon={ListChecks} label="Attempted" value={stats.totalAttempted} sub={`${stats.sessionsCompleted} sessions`} />
            <KpiCard icon={CheckCircle2} label="Correct" value={stats.correct} accent="text-green-500" />
            <KpiCard icon={XCircle} label="Wrong" value={stats.incorrect} accent="text-red-500" />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <BreakdownChart title="Accuracy by subject" data={stats.bySubject} labels={SUBJECT_LABELS} testid="chart-subject" />
            <BreakdownChart title="Accuracy by difficulty" data={stats.byDifficulty} labels={DIFFICULTY_LABELS} testid="chart-difficulty" />
          </div>

          <div className="mt-6 rounded-2xl border border-border bg-card p-6" data-testid="session-history">
            <h3 className="mb-4 font-heading text-lg font-semibold">Recent sessions</h3>
            {sessions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No completed sessions yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead className="text-center">Score</TableHead>
                    <TableHead className="text-center">Accuracy</TableHead>
                    <TableHead className="text-right">Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((s) => {
                    const acc = s.total ? Math.round((s.correct / s.total) * 100) : 0;
                    return (
                      <TableRow key={s.id}>
                        <TableCell className="text-sm">
                          {new Date(s.startedAt).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {s.mode === "TIMED" ? "Timed" : "Untimed"}
                        </TableCell>
                        <TableCell className="text-center text-sm font-medium">
                          {s.correct}/{s.total}
                        </TableCell>
                        <TableCell className="text-center text-sm font-semibold">{acc}%</TableCell>
                        <TableCell className="text-right font-mono-num text-sm text-muted-foreground">
                          {fmtTime(s.durationSec || 0)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
